/**
 * Collection sort dropdown (desktop) and bottom sheet (mobile).
 * Desktop: dropdown below button. Mobile: bottom sheet with backdrop, close button, header, list with dividers.
 */
(function () {
    const MOBILE_BREAKPOINT = 1024;

    const isMobile = () => window.innerWidth < MOBILE_BREAKPOINT;

    let stickyFrame = null;
    const updateStickyShadow = () => {
      stickyFrame = null;
      // The toolbar is sticky only below 1024px; desktop needs no layout measurement.
      if (!isMobile()) {
        document.querySelectorAll('.main-collection__toolbar.is-sticky').forEach((toolbar) => {
          toolbar.classList.remove('is-sticky');
        });
        return;
      }
      document.querySelectorAll('.main-collection__toolbar').forEach((toolbar) => {
        const styles = getComputedStyle(toolbar);
        const stickyTop = parseFloat(styles.top);
        const atTop = styles.position === 'sticky' && Number.isFinite(stickyTop)
          && Math.abs(toolbar.getBoundingClientRect().top - stickyTop) <= 1;
        toolbar.classList.toggle('is-sticky', atTop);
      });
    };
    const scheduleStickyShadow = () => {
      if (stickyFrame === null) stickyFrame = requestAnimationFrame(updateStickyShadow);
    };

    window.addEventListener('scroll', scheduleStickyShadow, { passive: true });
    window.addEventListener('resize', scheduleStickyShadow);
    window.addEventListener('pageshow', scheduleStickyShadow);
    document.addEventListener('shopify:section:load', scheduleStickyShadow);
    document.addEventListener('DOMContentLoaded', scheduleStickyShadow);
    scheduleStickyShadow();

    const initSortDropdown = (container) => {
      if (!container || container.getAttribute('data-sort-dropdown-ready') === 'true') return;
      container.setAttribute('data-sort-dropdown-ready', 'true');

      const button = container.querySelector('.main-collection__sort-button');
      const dropdown = container.querySelector('.main-collection__sort-list');
      const options = container.querySelectorAll('.main-collection__sort-option');
      const sheet = container.querySelector('[data-sort-sheet]');
      const backdrop = container.querySelector('[data-sort-sheet-backdrop]');
      const sheetCloseBtn = container.querySelector('[data-sort-sheet-close]');
      const sheetOptions = container.querySelectorAll('.main-collection__sort-sheet-option');
      const isMobileSort = container.hasAttribute('data-sort-mobile-container');
      let isDropdownOpen = false;
      let isSheetOpen = false;
      let sheetPosition;
      let backdropPosition;

      if (!button) return;

      const toggleDropdown = () => {
        if (isDropdownOpen) closeDropdown();
        else openDropdown();
      };

      const openDropdown = () => {
        if (!dropdown) return;
        isDropdownOpen = true;
        dropdown.classList.add('main-collection__sort-list--open');
        button.classList.add('main-collection__sort-button--open');
        button.setAttribute('aria-expanded', 'true');
        options.forEach((opt) => opt.setAttribute('tabindex', '0'));
        const arrow = button.querySelector('.main-collection__sort-arrow');
        if (arrow) arrow.style.transform = 'rotate(180deg)';
      };

      const closeDropdown = () => {
        if (!dropdown) return;
        isDropdownOpen = false;
        dropdown.classList.remove('main-collection__sort-list--open');
        button.classList.remove('main-collection__sort-button--open');
        button.setAttribute('aria-expanded', 'false');
        options.forEach((opt) => opt.setAttribute('tabindex', '-1'));
        const arrow = button.querySelector('.main-collection__sort-arrow');
        if (arrow) arrow.style.transform = 'rotate(0deg)';
      };

      const openSheet = () => {
        if (!sheet || !backdrop) return;
        // Escape the sticky toolbar's stacking context so the scrim covers header search.
        sheetPosition = document.createComment('Sort sheet position');
        backdropPosition = document.createComment('Sort backdrop position');
        sheet.before(sheetPosition);
        backdrop.before(backdropPosition);
        document.body.append(backdrop, sheet);
        isSheetOpen = true;
        backdrop.classList.add('backdrop-scrim--visible');
        sheet.classList.add('main-collection__sort-sheet--open');
        sheet.setAttribute('aria-hidden', 'false');
        backdrop.setAttribute('aria-hidden', 'false');
        button.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
        sheetOptions.forEach((opt) => opt.setAttribute('tabindex', '0'));
        /* Focus close control, not first option — avoids :focus styling that looked like a selection */
        if (sheetCloseBtn) sheetCloseBtn.focus();
      };

      const closeSheet = () => {
        if (!sheet || !backdrop) return;
        isSheetOpen = false;
        backdrop.classList.remove('backdrop-scrim--visible');
        sheet.classList.remove('main-collection__sort-sheet--open');
        sheet.setAttribute('aria-hidden', 'true');
        backdrop.setAttribute('aria-hidden', 'true');
        button.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
        sheetOptions.forEach((opt) => opt.setAttribute('tabindex', '-1'));
        sheetPosition?.replaceWith(sheet);
        backdropPosition?.replaceWith(backdrop);
        sheetPosition = null;
        backdropPosition = null;
        button.focus();
      };

      window.addEventListener('resize', () => {
        if (isSheetOpen && !isMobile()) closeSheet();
      });
      document.addEventListener('shopify:section:load', () => {
        if (isSheetOpen) closeSheet();
      });

      const applySort = (value) => {
        if (!value) return;
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('sort_by', value);
        window.location.href = currentUrl.toString();
      };

      const handleOptionClick = (option) => {
        const value = option.dataset.value;
        const text = option.textContent.trim();
        if (!value) return;

        const buttonText = button.querySelector('.main-collection__sort-button-text');
        if (buttonText) buttonText.textContent = text;

        const allOptions = container.querySelectorAll('.main-collection__sort-option');
        allOptions.forEach((opt) => {
          opt.classList.remove('main-collection__sort-option--selected');
          opt.setAttribute('aria-selected', 'false');
        });
        option.classList.add('main-collection__sort-option--selected');
        option.setAttribute('aria-selected', 'true');

        closeDropdown();
        closeSheet();
        applySort(value);
      };

      const handleSheetOptionClick = (option) => {
        const value = option.dataset.value;
        if (!value) return;
        sheetOptions.forEach((opt) => {
          opt.classList.remove('main-collection__sort-option--selected');
          opt.setAttribute('aria-selected', 'false');
        });
        option.classList.add('main-collection__sort-option--selected');
        option.setAttribute('aria-selected', 'true');
        closeSheet();
        applySort(value);
      };

      button.addEventListener('click', (e) => {
        e.stopPropagation();
        if (isMobileSort && isMobile()) {
          if (isSheetOpen) closeSheet();
          else openSheet();
        } else {
          toggleDropdown();
        }
      });

      if (dropdown && options.length) {
        options.forEach((option) => {
          option.addEventListener('click', (e) => {
            e.stopPropagation();
            handleOptionClick(option);
          });
          option.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              handleOptionClick(option);
            }
          });
        });
      }

      if (sheetOptions.length) {
        sheetOptions.forEach((option) => {
          option.addEventListener('click', (e) => {
            e.stopPropagation();
            handleSheetOptionClick(option);
          });
          option.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              handleSheetOptionClick(option);
            }
          });
        });
      }

      if (sheetCloseBtn) sheetCloseBtn.addEventListener('click', closeSheet);
      if (backdrop) backdrop.addEventListener('click', closeSheet);

      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (isSheetOpen) {
          e.preventDefault();
          closeSheet();
        } else if (isDropdownOpen) {
          e.preventDefault();
          closeDropdown();
          button.focus();
        }
      });

      document.addEventListener('click', (e) => {
        if (!container.contains(e.target) && isDropdownOpen) closeDropdown();
      });

      button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          if (isMobileSort && isMobile()) {
            if (!isSheetOpen) openSheet();
          } else if (!isDropdownOpen) {
            openDropdown();
            const firstOption = options[0];
            if (firstOption) firstOption.focus();
          }
        }
      });

      if (dropdown && options.length) {
        const handleDropdownKeydown = (e) => {
          if (e.key === 'ArrowDown' && isDropdownOpen) {
            e.preventDefault();
            const idx = Array.from(options).findIndex(opt => opt === document.activeElement);
            const next = idx < options.length - 1 ? idx + 1 : 0;
            options[next]?.focus();
          } else if (e.key === 'ArrowUp' && isDropdownOpen) {
            e.preventDefault();
            const idx = Array.from(options).findIndex(opt => opt === document.activeElement);
            const prev = idx > 0 ? idx - 1 : options.length - 1;
            options[prev]?.focus();
          }
        };
        options.forEach((opt) => opt.addEventListener('keydown', handleDropdownKeydown));
      }
    };

    document.addEventListener('shopify:section:load', () => {
      document.querySelectorAll('.main-collection__sort-dropdown').forEach(initSortDropdown);
    });
    document.addEventListener('filter:update', () => {
      document.querySelectorAll('.main-collection__sort-dropdown').forEach(initSortDropdown);
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        document.querySelectorAll('.main-collection__sort-dropdown').forEach(initSortDropdown);
      });
    } else {
      document.querySelectorAll('.main-collection__sort-dropdown').forEach(initSortDropdown);
    }
  })();
